// One view of how a user's Channels, Schemas and Sources connect:
//   Source (mapping)  --spec.channel-->  Channel  --templateIds-->  Schema (template)
// Anything not connected to a channel is listed separately, so nothing is hidden.
import type { Channel, Mapping, Template } from '../types';

export interface DataRow {
  channel: Channel;
  schemas: Template[];
  sources: Mapping[];
}

export interface DataMap {
  rows: DataRow[];
  looseSchemas: Template[];
  looseSources: Mapping[];
}

const norm = (s: string) => s.replace(/^@/, '').toLowerCase();

export function buildDataMap(channels: Channel[], templates: Template[], mappings: Mapping[]): DataMap {
  const tplBy = new Map(templates.map((t) => [norm(t.nickname), t]));
  const usedSchemas = new Set<string>();
  const usedSources = new Set<string>();
  const rows: DataRow[] = channels.map((channel) => {
    const schemas = (channel.templateIds ?? []).map((id) => tplBy.get(norm(id))).filter((t): t is Template => !!t);
    schemas.forEach((t) => usedSchemas.add(norm(t.nickname)));
    const sources = mappings.filter((m) => {
      const target = typeof m.spec?.channel === 'string' ? norm(m.spec.channel as string) : '';
      return target !== '' && target === norm(channel.nickname);
    });
    sources.forEach((m) => usedSources.add(norm(m.nickname)));
    return { channel, schemas, sources };
  });
  return {
    rows,
    looseSchemas: templates.filter((t) => !usedSchemas.has(norm(t.nickname))),
    looseSources: mappings.filter((m) => !usedSources.has(norm(m.nickname))),
  };
}

/** Short, honest description of how a Source gets its data. */
export function sourceKind(m: Mapping): string {
  const p = m.spec?.provider;
  if (p === 'sec-xbrl') return 'SEC EDGAR · daily';
  if (p === 'github-api') return 'GitHub API · daily';
  if (p === 'web-auto') return 'Web document · daily';
  if (m.sourceType === 'webhook') return 'Webhook';
  if (m.sourceType.startsWith('scrape_')) {
    const s = Number(m.spec?.intervalSec);
    return `Polled${Number.isFinite(s) && s > 0 ? ` every ${s >= 3600 ? `${Math.round(s / 3600)} h` : `${Math.round(s / 60)} min`}` : ''}`;
  }
  return m.sourceType || 'Source';
}
